import { inject, Injectable } from "@angular/core";
import { lastValueFrom } from "rxjs";

import {
	CollectionBundle,
	DataBundle,
	DataBundleWithActions,
	FileDownloadBundle,
	PagedCollectionBundle,
	PagingParams,
	S3FileUploadRequestBundle,
} from "@intocare/datatypes/lib/bundle";
import { omitNullable } from "@intocare/util";

import { AssessmentCreationDto } from "./assessment-creation.dto";
import {
	DeleteAssessmentUploadDto,
	RequestAssessmentUploadDto,
	SubmitAssessmentUploadDto,
} from "./assessment-upload.dto";
import {
	AssessmentDetailDtoBundle,
	AssessmentPlannedDateSetDto,
	AssessmentPlannedDateUnsetDto,
	AssessmentPlannedDateUpdateDto,
	AssessmentSummaryDto,
	SubmitAssessmentResponseDto,
	SubmittedAssessmentDetailDto,
} from "./assessment.dto";
import { AttachedDocumentDto } from "./attached-document.dto";
import {
	CareDataContext,
	FollowUpPlanFilters,
	ObservationFilters,
	ObservationOfTypeFallReportFilters,
} from "./care-data";
import { EvaluationDetailDto, EvaluationSummaryDto } from "./evaluation.dto";
import { FollowUpPlanStatus } from "./follow-up-plan";
import { FollowUpPlanCreationDto } from "./follow-up-plan-creation.dto";
import { FollowUpPlanEditDto } from "./follow-up-plan-edit.dto";
import {
	DraftFollowUpPlanEvaluationEditDto,
	FinalizedFollowUpPlanEvaluationEditDto,
	FollowUpPlanEvaluationCreationDto,
} from "./follow-up-plan-evaluation.dto";
import { FollowUpPlanObservationLinkDto } from "./follow-up-plan-observation-links.dto";
import { RequestFollowUpPlanUploadDto, SubmitFollowUpPlanUploadDto } from "./follow-up-plan-upload.dto";
import { FollowUpPlanDetailsDto, FollowUpPlanForSiteSummaryDto } from "./follow-up-plan.dto";
import {
	ObservationOfTypeFallCreationDto,
	ObservationOfTypeIncontinenceCreationDto,
	ObservationOfTypeInfectionCreationDto,
	ObservationOfTypeNutritionAndHydrationCreationDto,
	ObservationOfTypeOtherCreationDto,
	ObservationOfTypePhysicalComplaintCreationDto,
} from "./observation-creation.dto";
import {
	ObservationOfTypeFallEditDto,
	ObservationOfTypeIncontinenceEditDto,
	ObservationOfTypeInfectionEditDto,
	ObservationOfTypeNutritionAndHydrationEditDto,
	ObservationOfTypeOtherEditDto,
	ObservationOfTypePhysicalComplaintEditDto,
} from "./observation-edit.dto";
import { ObservationHistoryEventDto } from "./observation-history-event.dto";
import { ObservationLinkType } from "./observation-links";
import {
	ObservationLinkDto,
	ObservationOfTypeFallWithLinksDto,
	ObservationOfTypeIncontinenceWithLinksDto,
	ObservationOfTypeInfectionWithLinksDto,
	ObservationOfTypeNutritionAndHydrationWithLinksDto,
	ObservationOfTypeOtherWithLinksDto,
	ObservationOfTypePhysicalComplaintWithLinksDto,
} from "./observation-links.dto";
import { ObservationOfTypeFallDto, ObservationSummaryDto, ObservationSummaryWithUserDto } from "./observations.dto";
import { RestrictiveMeasuresForResidentDto } from "./restrictive-measures.dto";
import { AppHttpClient } from "../../shared/base/app-http-client";

/** Maps the observation filters to the snake_case query parameters of the care API. */
function observationFilterParams({ observationType, startOccurredDate, endOccurredDate }: ObservationFilters) {
	return {
		observation_type: observationType,
		start_occurred_date: startOccurredDate,
		end_occurred_date: endOccurredDate,
	};
}

@Injectable({
	providedIn: "root",
})
export class CareDataApi {
	private readonly http = inject(AppHttpClient);

	getObservationSummariesByResidentId(
		context: CareDataContext,
		params: { residentId: string; filters: ObservationFilters },
	) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundle<ObservationSummaryDto>>>(
				`/care/portal/sites/${context.siteId}/observation-summaries-by-resident-id`,
				{ params: omitNullable({ resident_id: params.residentId, ...observationFilterParams(params.filters) }) },
			),
		);
	}

	getObservationSummaryById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundle<ObservationSummaryWithUserDto>>(
				`/care/portal/sites/${context.siteId}/hateoas-observation-summary-by-id`,
				{
					params: { id: params.observationId },
				},
			),
		);
	}

	getHateoasFallObservationById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<ObservationOfTypeFallWithLinksDto, "update">>(
				`/care/portal/sites/${context.siteId}/hateoas-fall-observation-by-id`,
				{ params: { id: params.observationId } },
			),
		);
	}

	createFallObservation(context: CareDataContext, formData: ObservationOfTypeFallCreationDto) {
		return lastValueFrom(
			this.http.post<{ id: string }>(`/care/portal/sites/${context.siteId}/create-fall-observation`, formData),
		);
	}

	editFallObservation(context: CareDataContext, formData: ObservationOfTypeFallEditDto) {
		return lastValueFrom(this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-fall-observation`, formData));
	}

	getIncontinenceObservationById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<ObservationOfTypeIncontinenceWithLinksDto, "update">>(
				`/care/portal/sites/${context.siteId}/hateoas-incontinence-observation-by-id`,
				{ params: { id: params.observationId } },
			),
		);
	}

	createIncontinenceObservation(context: CareDataContext, formData: ObservationOfTypeIncontinenceCreationDto) {
		return lastValueFrom(
			this.http.post<{ id: string }>(`/care/portal/sites/${context.siteId}/create-incontinence-observation`, formData),
		);
	}

	editIncontinenceObservation(context: CareDataContext, formData: ObservationOfTypeIncontinenceEditDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-incontinence-observation`, formData),
		);
	}

	getInfectionObservationById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<ObservationOfTypeInfectionWithLinksDto, "update">>(
				`/care/portal/sites/${context.siteId}/infection-observation-by-id`,
				{ params: { id: params.observationId } },
			),
		);
	}

	editInfectionObservation(context: CareDataContext, formData: ObservationOfTypeInfectionEditDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-infection-observation`, formData),
		);
	}

	createInfectionObservation(context: CareDataContext, formData: ObservationOfTypeInfectionCreationDto) {
		return lastValueFrom(
			this.http.post<{ id: string }>(`/care/portal/sites/${context.siteId}/create-infection-observation`, formData),
		);
	}

	getHateoasOtherObservationById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<ObservationOfTypeOtherWithLinksDto, "update">>(
				`/care/portal/sites/${context.siteId}/hateoas-other-observation-by-id`,
				{ params: { id: params.observationId } },
			),
		);
	}

	createOtherObservation(context: CareDataContext, formData: ObservationOfTypeOtherCreationDto) {
		return lastValueFrom(
			this.http.post<{ id: string }>(`/care/portal/sites/${context.siteId}/create-other-observation`, formData),
		);
	}

	editOtherObservation(context: CareDataContext, formData: ObservationOfTypeOtherEditDto) {
		return lastValueFrom(this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-other-observation`, formData));
	}

	getLinkableObservations(
		context: CareDataContext,
		params: {
			source: { residentId: string; observationId: string };
			linkType: ObservationLinkType;
			filters: ObservationFilters;
		},
	) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundleWithActions<ObservationSummaryDto, "link">>>(
				`/care/portal/sites/${context.siteId}/linkable-observations`,
				{
					params: omitNullable({
						resident_id: params.source.residentId,
						observation_id: params.source.observationId,
						relation: params.linkType,
						...observationFilterParams(params.filters),
					}),
				},
			),
		);
	}

	getLinkableObservationsForFollowUpPlan(
		context: CareDataContext,
		params: {
			source: { residentId: string; followUpPlanId: string };
			filters: ObservationFilters;
		},
	) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundleWithActions<ObservationSummaryDto, "link">>>(
				`/care/portal/sites/${context.siteId}/linkable-observations-for-follow-up-plan`,
				{
					params: omitNullable({
						resident_id: params.source.residentId,
						follow_up_plan_id: params.source.followUpPlanId,
						...observationFilterParams(params.filters),
					}),
				},
			),
		);
	}

	createLinkBetweenObservations(context: CareDataContext, formData: ObservationLinkDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/create-link-between-observations`, formData),
		);
	}

	deleteLinkBetweenObservations(context: CareDataContext, formData: ObservationLinkDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/delete-link-between-observations`, formData),
		);
	}

	getFallObservationsBySiteId(
		context: CareDataContext,
		params: { paging: PagingParams; filters: ObservationOfTypeFallReportFilters },
	) {
		return lastValueFrom(
			this.http.get<PagedCollectionBundle<DataBundle<ObservationOfTypeFallDto>>>(
				`/care/portal/sites/${context.siteId}/fall-observations-by-site-id`,
				{
					params: omitNullable({
						...params.paging,
						start_occurred_date: params.filters.startOccurredDate,
						end_occurred_date: params.filters.endOccurredDate,
					}),
				},
			),
		);
	}

	downloadSiteFallObservationsXlsx(
		context: CareDataContext,
		formData: { filters: ObservationOfTypeFallReportFilters },
	) {
		return lastValueFrom(
			this.http.post<FileDownloadBundle>(
				`/care/portal/sites/${context.siteId}/download-site-fall-observations-xlsx`,
				formData.filters,
			),
		);
	}

	getObservationHistory(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundle<ObservationHistoryEventDto>>>(
				`/care/portal/sites/${context.siteId}/hateoas-observation-history`,
				{
					params: { observation_id: params.observationId },
				},
			),
		);
	}

	createFollowUpPlan(context: CareDataContext, formData: FollowUpPlanCreationDto) {
		return lastValueFrom(
			this.http.post<{ id: string }>(`/care/portal/sites/${context.siteId}/create-follow-up-plan`, formData),
		);
	}

	getFollowUpPlansForSite(
		context: CareDataContext,
		params: { residentId?: string; filters: FollowUpPlanFilters; paging: PagingParams },
	) {
		return lastValueFrom(
			this.http.get<PagedCollectionBundle<DataBundleWithActions<FollowUpPlanForSiteSummaryDto, never>>>(
				`/care/portal/sites/${context.siteId}/follow-up-plans-for-site`,
				{
					params: omitNullable({
						...(params.residentId && { resident_id: params.residentId }),
						care_themes: params.filters.careThemes,
						follow_up_plan_status: params.filters.followUpPlanStatus,
						start_follow_up_date: params.filters.startFollowUpDate,
						end_follow_up_date: params.filters.endFollowUpDate,
						no_follow_up_date: params.filters.noFollowUpDate,
						department_id: params.filters.departmentId,
						follow_up_plan_title: params.filters.followUpPlanTitle,
						active_measure_types: params.filters.activeMeasureTypes,
						...params.paging,
					}),
				},
			),
		);
	}

	getFollowUpPlanById(context: CareDataContext, params: { followUpPlanId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<FollowUpPlanDetailsDto, "update">>(
				`/care/portal/sites/${context.siteId}/follow-up-plan-by-id`,
				{
					params: { id: params.followUpPlanId },
				},
			),
		);
	}

	getFollowUpPlanDocuments(context: CareDataContext, params: { followUpPlanId: string }) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundle<AttachedDocumentDto>>>(
				`/care/portal/sites/${context.siteId}/uploads-for-follow-up-plan`,
				{ params: { follow_up_plan_id: params.followUpPlanId } },
			),
		);
	}

	getFollowUpPlanDocumentUrl(context: CareDataContext, params: { followUpPlanId: string; documentId: string }) {
		return lastValueFrom(
			this.http.get<FileDownloadBundle>(`/care/portal/sites/${context.siteId}/url-for-follow-up-plan-upload`, {
				params: { follow_up_plan_id: params.followUpPlanId, upload_id: params.documentId },
			}),
		);
	}

	deleteFollowUpPlanDocument(
		context: CareDataContext,
		formData: { followUpPlanId: string; followUpPlanVersion: number; uploadId: string; uploadVersion: number },
	) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/delete-follow-up-plan-upload`, formData),
		);
	}

	setFollowUpDate(
		context: CareDataContext,
		params: { followUpPlanId: string },
		formData: { followUpDate: string | null; version: number },
	) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/set-follow-up-date`, formData, {
				params: { follow_up_plan_id: params.followUpPlanId },
			}),
		);
	}

	requestFollowUpPlanUpload(context: CareDataContext, formData: RequestFollowUpPlanUploadDto) {
		return this.http.post<S3FileUploadRequestBundle>(
			`/care/portal/sites/${context.siteId}/request-follow-up-plan-upload`,
			formData,
		);
	}

	submitFollowUpPlanUpload(context: CareDataContext, formData: SubmitFollowUpPlanUploadDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/submit-follow-up-plan-upload`, formData),
		);
	}

	createFollowUpPlanEvaluation(context: CareDataContext, formData: FollowUpPlanEvaluationCreationDto) {
		return lastValueFrom(
			this.http.post<{ id: string }>(`/care/portal/sites/${context.siteId}/create-follow-up-plan-evaluation`, formData),
		);
	}

	editDraftFollowUpPlanEvaluation(context: CareDataContext, formData: DraftFollowUpPlanEvaluationEditDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-draft-follow-up-plan-evaluation`, formData),
		);
	}

	editFinalizedFollowUpPlanEvaluation(context: CareDataContext, formData: FinalizedFollowUpPlanEvaluationEditDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-finalized-follow-up-plan-evaluation`, formData),
		);
	}

	getFollowUpPlanEvaluationById(context: CareDataContext, params: { evaluationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<EvaluationDetailDto, "update">>(
				`/care/portal/sites/${context.siteId}/follow-up-plan-evaluation-by-id`,
				{
					params: { id: params.evaluationId },
				},
			),
		);
	}

	getEvaluationsForFollowUpPlan(context: CareDataContext, params: { followUpPlanId: string }) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundle<EvaluationSummaryDto>>>(
				`/care/portal/sites/${context.siteId}/evaluations-for-follow-up-plan`,
				{ params: { follow_up_plan_id: params.followUpPlanId } },
			),
		);
	}

	getLinkedObservationsForFollowUpPlan(context: CareDataContext, params: { followUpPlanId: string }) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundle<ObservationSummaryDto>>>(
				`/care/portal/sites/${context.siteId}/linked-observations-for-follow-up-plan`,
				{ params: { follow_up_plan_id: params.followUpPlanId } },
			),
		);
	}

	linkObservationToFollowUpPlan(
		context: CareDataContext,
		formData: { followUpPlanId: string; observationIds: string[] },
	) {
		return lastValueFrom(
			this.http.post<void>(
				`/care/portal/sites/${context.siteId}/create-link-between-follow-up-plan-and-observation`,
				formData,
			),
		);
	}

	createPhysicalComplaintObservation(
		context: CareDataContext,
		formData: ObservationOfTypePhysicalComplaintCreationDto,
	) {
		return lastValueFrom(
			this.http.post<{ id: string }>(
				`/care/portal/sites/${context.siteId}/create-physical-complaint-observation`,
				formData,
			),
		);
	}

	getPhysicalComplaintObservationById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<ObservationOfTypePhysicalComplaintWithLinksDto, "update">>(
				`/care/portal/sites/${context.siteId}/physical-complaint-observation-by-id`,
				{ params: { id: params.observationId } },
			),
		);
	}

	editPhysicalComplaintObservation(context: CareDataContext, formData: ObservationOfTypePhysicalComplaintEditDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-physical-complaint-observation`, formData),
		);
	}

	getHateoasObservationSummaryById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundle<ObservationSummaryWithUserDto>>(
				`/care/portal/sites/${context.siteId}/hateoas-observation-summary-by-id`,
				{
					params: { id: params.observationId },
				},
			),
		);
	}

	setFollowUpPlanStatus(
		context: CareDataContext,
		params: { followUpPlanId: string; status: FollowUpPlanStatus; version: number },
	) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/set-follow-up-plan-status`, params),
		);
	}

	createNutritionAndHydrationObservation(
		context: CareDataContext,
		formData: ObservationOfTypeNutritionAndHydrationCreationDto,
	) {
		return lastValueFrom(
			this.http.post<{ id: string }>(
				`/care/portal/sites/${context.siteId}/create-nutrition-and-hydration-observation`,
				formData,
			),
		);
	}

	getNutritionAndHydrationObservationById(context: CareDataContext, params: { observationId: string }) {
		return lastValueFrom(
			this.http.get<DataBundleWithActions<ObservationOfTypeNutritionAndHydrationWithLinksDto, "update">>(
				`/care/portal/sites/${context.siteId}/nutrition-and-hydration-observation-by-id`,
				{ params: { id: params.observationId } },
			),
		);
	}

	editNutritionAndHydrationObservation(
		context: CareDataContext,
		formData: ObservationOfTypeNutritionAndHydrationEditDto,
	) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-nutrition-and-hydration-observation`, formData),
		);
	}

	editFollowUpPlan(context: CareDataContext, formData: FollowUpPlanEditDto) {
		return lastValueFrom(this.http.post<void>(`/care/portal/sites/${context.siteId}/edit-follow-up-plan`, formData));
	}

	deleteFollowUpPlanObservationLink(context: CareDataContext, formData: FollowUpPlanObservationLinkDto) {
		return lastValueFrom(
			this.http.post<void>(
				`/care/portal/sites/${context.siteId}/delete-link-between-follow-up-plan-and-observation`,
				formData,
			),
		);
	}

	getAssessmentSummaries(context: CareDataContext, params: { residentId: string }) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundle<AssessmentSummaryDto>>>(
				`/care/portal/sites/${context.siteId}/assessment-summaries`,
				{ params: { resident_id: params.residentId } },
			),
		);
	}

	getAssessmentByType(context: CareDataContext, params: { assessmentType: string; residentId: string }) {
		return lastValueFrom(
			this.http.get<AssessmentDetailDtoBundle>(`/care/portal/sites/${context.siteId}/assessment-by-type`, {
				params: { assessment_type: params.assessmentType, resident_id: params.residentId },
			}),
		);
	}

	setAssessmentPlanningDate(context: CareDataContext, body: AssessmentPlannedDateSetDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/set-assessment-planning-date`, body),
		);
	}

	updateAssessmentPlanningDate(context: CareDataContext, body: AssessmentPlannedDateUpdateDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/update-assessment-planning-date`, body),
		);
	}

	unsetAssessmentPlanningDate(context: CareDataContext, body: AssessmentPlannedDateUnsetDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/unset-assessment-planning-date`, body),
		);
	}

	submitAssessment(context: CareDataContext, formData: AssessmentCreationDto) {
		return lastValueFrom(
			this.http.post<DataBundle<SubmitAssessmentResponseDto>>(
				`/care/portal/sites/${context.siteId}/submit-assessment`,
				formData,
			),
		);
	}

	archiveSubmittedAssessment(context: CareDataContext, body: { submittedAssessmentId: string; version: number }) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/archive-submitted-assessment`, body),
		);
	}

	enableSubmittedAssessment(context: CareDataContext, body: { submittedAssessmentId: string; version: number }) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/enable-submitted-assessment`, body),
		);
	}

	getSubmittedAssessmentDetail(context: CareDataContext, params: { submittedAssessmentId: string }) {
		return lastValueFrom(
			this.http.get<SubmittedAssessmentDetailDto>(
				`/care/portal/sites/${context.siteId}/submitted-assessment-detail-by-id`,
				{ params: { submitted_assessment_id: params.submittedAssessmentId } },
			),
		);
	}

	getSubmittedAssessmentDocuments(context: CareDataContext, params: { submittedAssessmentId: string }) {
		return lastValueFrom(
			this.http.get<CollectionBundle<DataBundle<AttachedDocumentDto>>>(
				`/care/portal/sites/${context.siteId}/uploads-for-assessment-attachment`,
				{ params: { submitted_assessment_id: params.submittedAssessmentId } },
			),
		);
	}

	uploadSubmittedAssessmentDocument(context: CareDataContext, formData: RequestAssessmentUploadDto) {
		return this.http.post<S3FileUploadRequestBundle>(
			`/care/portal/sites/${context.siteId}/request-assessment-attachment-upload`,
			formData,
		);
	}

	submitSubmittedAssessmentDocumentUploads(context: CareDataContext, formData: SubmitAssessmentUploadDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/submit-assessment-attachment-upload`, formData),
		);
	}

	getSubmittedAssessmentDocumentUrl(
		context: CareDataContext,
		params: { submittedAssessmentId: string; documentId: string },
	) {
		return lastValueFrom(
			this.http.get<FileDownloadBundle>(`/care/portal/sites/${context.siteId}/url-for-assessment-attachment-upload`, {
				params: { submitted_assessment_id: params.submittedAssessmentId, upload_id: params.documentId },
			}),
		);
	}

	deleteSubmittedAssessmentDocument(context: CareDataContext, formData: DeleteAssessmentUploadDto) {
		return lastValueFrom(
			this.http.post<void>(`/care/portal/sites/${context.siteId}/delete-assessment-attachment-upload`, formData),
		);
	}

	getRestrictiveMeasuresForResident(context: CareDataContext, params: { residentId: string }) {
		return lastValueFrom(
			this.http.get<DataBundle<RestrictiveMeasuresForResidentDto>>(
				`/care/portal/sites/${context.siteId}/restrictive-measures-for-resident`,
				{
					params: { resident_id: params.residentId },
				},
			),
		);
	}
}
